import { FYP_PAGE_ID, FYP_PAGE_URL } from "@websim/core/src/managed/fyp";
import {
  LANDING_PAGE_ID,
  LANDING_PAGE_URL,
} from "@websim/core/src/managed/landing";

const HOMEPAGE_SOCIAL_LINKS = [
  "https://twitter.com/websim_ai",
  "https://discord.gg/websim",
  "https://www.reddit.com/r/WebSim",
  "https://websim.ai/sponsorship",
  "https://websim.com/sponsorship",
];

export function isHomepageSocialLink(href: string): boolean {
  return HOMEPAGE_SOCIAL_LINKS.includes(href);
}

export type ReservedUrl =
  | {
      type: "project";
      project_id: string;
      version?: number;
      search?: string;
    }
  | {
      type: "site";
      site_id: string;
      search?: string;
    }
  | {
      type: "profile";
      username: string;
      search?: string;
    }
  | {
      type: "slug";
      username: string;
      version?: number;
      slug: string;
      search?: string;
    }
  | {
      type: "url";
      url: string;
    };

export function getReservedWebsimUrl(href: string): ReservedUrl | void {
  if (
    href.startsWith("discord.com") ||
    href.startsWith("http://discord.com") ||
    href.startsWith("https://discord.com")
  ) {
    return { type: "url", url: href };
  }

  if (href === "https://websim.com/auth/login") {
    return { type: "url", url: "/auth/login" };
  }

  if (isHomepageSocialLink(href)) {
    return { type: "url", url: href };
  }

  const websimUrl = normalizeWebsimUrl(href);
  if (websimUrl) {
    if (href === LANDING_PAGE_URL) {
      return { type: "site", site_id: LANDING_PAGE_ID };
    }

    if (href === FYP_PAGE_URL) {
      return { type: "site", site_id: FYP_PAGE_ID };
    }

    let match: RegExpMatchArray | null;
    match = websimUrl.match(
      /^https:\/\/websim\.(?:ai|com)\/c\/([a-zA-Z0-9]{17})(\?.*)?$/
    );

    if (match) {
      const site_id = match[1];
      const search = match[2];
      return { type: "site", site_id, search };
    }

    match = websimUrl.match(
      /^https:\/\/websim\.(?:ai|com)\/p\/([a-z0-9_-]{20})(\/\d+)?(\?.*)?$/
    );

    if (match) {
      const projectId = match[1];
      const version = match[2]
        ? parseInt(match[2].slice(1)) || undefined
        : undefined;
      const search = match[3];

      return {
        type: "project",
        project_id: projectId,
        version,
        search,
      };
    }

    match = websimUrl.match(
      /^https:\/\/websim\.(?:ai|com)\/@([^?/#]+)\/([^?/#]+)(\/\d+)?(\?.*)?$/
    );

    if (match) {
      const username = match[1];
      const slug = match[2];
      const version = match[3]
        ? parseInt(match[3].slice(1)) || undefined
        : undefined;
      const search = match[4];
      return { type: "slug", username, slug, version, search };
    }

    match = websimUrl.match(
      /^https:\/\/websim\.(?:ai|com)\/@([^?/#]+)(\?.*)?$/
    );

    if (match) {
      const username = match[1];
      const search = match[2];
      return { type: "profile", username, search };
    }
  }
}

function normalizeWebsimUrl(href: string): string | void {
  // If the href is a relative URL, prepend 'https://websim.com'
  if (href.startsWith("/")) {
    return `https://websim.com${href}`;
  }

  if (href.startsWith("@")) {
    return `https://websim.com/${href}`;
  }

  const websimSplit = href.split("websim.com");
  if (websimSplit.length < 2) {
    return;
  }
  const prefix = websimSplit[0];
  const pathName = websimSplit.slice(1).join("websim.com");
  if (prefix === "" || prefix === "http://" || prefix === "https://") {
    return `https://websim.com${pathName}`;
  }
  return;
}
